import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";

import { colors, theme } from "theme";
import { Error, Wrapper } from "./styles";
import { Icons, Ptag } from "..";
import { useEffect, useMemo } from "react";
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { formatCtcValue } from "utlis/utils";

/**
 * A component that renders a select menu.
 * @param {object} props - The props object.
 * @param {string} props.label - The label for the select menu.
 * @param {function} props.onChange - A callback method that will be called when the selected value changes.
 *                                    The updated value will be passed as an argument to the callback method.
 * @param {Array<object>} props.options - An array of objects containing label and title keys with string values.
 */

const BaseDropdown = ({
  options,
  label,
  disabled,
  variant,
  error,
  showNone,
  formikProps,
  fontColor,
  fontSize,
  labelStyle,
  labelFontSize = 14,
  multiple,
  staticLabel,
  width,
  glassBorder=false,
  dropdownIconColor="#231A3D"
}) => {
  const { values, handleChange, name, errors, touched } = formikProps;

  function getNestedValue(obj, path, context = {}) {
    // First, dynamically replace context variables if any
    const resolvedPath = path.replace(
      /\[\$(.*?)\]/g,
      (match, p1) => `[${context[p1]}]`
    );

    // Split the path into parts considering the array indices followed directly by property names
    const parts = resolvedPath.split(/\.|\[(?=\d+\])|\](?=\w)/).filter(Boolean);

    return parts.reduce((acc, part) => {
      // Normalize array access and property names
      const normalizedPart = part.replace(/[\[\]']+/g, "");
      return acc && acc[normalizedPart] !== undefined
        ? acc[normalizedPart]
        : undefined;
    }, obj);
  }

  const borderStyling = () => {
    if (glassBorder) {
      return {
        border: "1px solid rgba(255, 255, 255, 1)",
        boxShadow: "inset 2px 2px 5px rgba(0, 0, 0, 0.08), inset -1px -1px 3px rgba(255, 255, 255, 0.9)"
      }
    } else {
      return {
        borderColor: `${errors.hasOwnProperty(name) && touched[name] && errors[name]
            ? "red"
            : theme.primaryColor
          } !important`,
      }
    }
  }

  return (
    <Wrapper>
      {staticLabel && (
        <span id="static-label">
          <Ptag color={colors.primaryColor} fontSize="14px" marginBottom="12px" fontFamily="Montserrat">
            {staticLabel}
          </Ptag>
        </span>
      )}
      <FormControl
        sx={{
          width:width?width: "100%",
          minHeight: "inherit",
          "& label": {
            fontSize: `${labelFontSize}px !important`,
            transition: "0.1s ease-in-out all",
            ...labelStyle,
          },
        }}
        error={error}
      >
        <InputLabel
          sx={{
            color: theme.defaultFontColor,
            fontSize: `${fontSize ? fontSize + "px" : "12px"} !important`,
            fontFamily: "Montserrat",
            textAlign: "center",
            marginLeft: variant === "standard" ? "-6px" : "0px",
          }}
        >
          {label}
        </InputLabel>
        <Select
          // IconComponent={<Icons icon="chevron_down" />}
          IconComponent={(props) => (disabled ? null : <DropdownIcon dropdownIconColor={dropdownIconColor} />)}
          disabled={disabled}
          label={label}
          variant={variant ? variant : "outlined"}
          value={getNestedValue(values, name)}
          // value={values[name]}
          name={name}
          onChange={handleChange}
          multiple={multiple}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 200,
                marginTop: 8,
                border: "1px solid rgba(255, 255, 255, 1)",
                boxShadow: "inset 2px 2px 5px rgba(0, 0, 0, 0.08), inset -1px -1px 3px rgba(255, 255, 255, 0.9)",
                backgroundColor: "#f6f6f7"
              },
            },
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            getContentAnchorEl: null,
          }}
          sx={{
            width: "100%",
            borderRadius: "10px",
            "& .MuiOutlinedInput-notchedOutline": borderStyling(),

            // Remove border change on Hover
            "&:hover .MuiOutlinedInput-notchedOutline": {
              border: "1px solid rgba(255, 255, 255, 1)",
            },
            
            // Remove focus outline/border expansion when clicked
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "1px solid rgba(255, 255, 255, 1)",
            },
            
            "& .MuiSelect-standard": {
              borderBottom: `1px solid ${theme.primaryColor}`,
            },
            "& .MuiSelect-standard": {
              borderBottom: `1px solid ${theme.primaryColor}`,
            },
            "& .MuiSelect-select": {
              padding: "1.9rem",
              marginLeft: !variant ? "12px" : "0px",
              fontSize: '14px !important',
              color: `${disabled ? "#4a4a4a" : theme.defaultFontColor}`,
              fontFamily: "Montserrat",
              paddingLeft: "6px",
              minHeight: "inherit !important",
            },
            "& .MuiInputBase-input::placeholder": {
              fontSize: "14px !important",
              fontFamily: "Montserrat",
            },
          }}
        >
          {showNone && (
            <MenuItem
              value=""
              sx={{
                fontSize: `${fontSize ? fontSize : "12"}px`,
                fontFamily: "Montserrat",
              }}
            >
              <em>None</em>
            </MenuItem>
          )}
          {options &&
            options.map((option, index) => (
              <MenuItem
                key={index.toString()}
                sx={{
                  fontSize: "14px",
                  fontFamily: "Montserrat",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
                value={option.value}
              >
                {formatCtcValue(option.label)}
                {multiple &&
                getNestedValue(values, name)?.includes(option.value) ? (
                  <CheckBoxIcon
                    style={{
                      fontSize: "18px",
                      color: "black",
                    }}
                  />
                ) : multiple && (
                  <CheckBoxOutlineBlankIcon
                    style={{
                      fontSize: "18px",
                      color: "black",
                    }}
                  />
                )}
              </MenuItem>
            ))}
        </Select>
        {errors[name] && touched[name] && <Error>{errors[name]}</Error>}
      </FormControl>
    </Wrapper>
  );
};

const DropdownIcon = ({ dropdownIconColor }) => {
  return (
    <svg
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      width={20}
      height={20}
      style={{
        position: "absolute",
        right: "12px",
        userSelect: "none",
        pointerEvents: "none",
        fill: dropdownIconColor,
        color: dropdownIconColor,
      }}
      viewBox="0 0 20 20"
    >
      <title>chevron-down</title>
      <path
        fill={dropdownIconColor}
        color={dropdownIconColor}
        d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"
      />
    </svg>
  );
};

export default BaseDropdown;
